import React, { useContext, useEffect, useState } from 'react';
import { Loader } from 'semantic-ui-react';
import Avatar from '../../../components/components/Avatar';
import UserContext from '../../../contexts/UserContext';
import './style.less';
import { Registrant } from '../../types/Registrant';
import RedirectContext from '../../../contexts/RedirectContext';
import { getFamilyList } from '../../../services/family';
import sendToast from '../../../utils/Toast';
import ImageCropModal from '../../../components/components/ImageCropModal';
import { updateResident } from '../../../services/family';
import { converterDataURItoBlob, uploadToSignedUrl } from '../../../services/imageUpload';
import { fetchSignedUrl } from '../../../utils/imageUpload';
import { residentImageFolder, speak2CdnDevBucketUrl } from '../../../utils/s3Buckets';
import { fetchGroups } from '../../services/Groups';
import { ProfileTab } from '../../types/MyProfile';
import settleAll from '../../../utils/settleAll';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { getResidentDisplayFirstName, getResidentDisplayFullName, getResidentDisplayImage, getResidentDisplayImages, getResidentDisplayLastName } from '../../utils/residentIdentity';

interface Props {
    activeTab: ProfileTab;
    onTabChange: (tab: ProfileTab) => void;
}

const AllConnectedResidents = ({ activeTab, onTabChange }: Props) => {
    const { userObject, setUser } = useContext(UserContext);
    const toggleRedirect = useContext(RedirectContext);
    const { resolvedShowPaymentsEntry } = useNeighborsPaymentAccess();

    const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
    const [imageEditor, setImageEditor] = useState<any>();
    const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState('');
    const [imageName, setImageName] = useState<string>('');

    const [groupsCount, setGroupsCount] = useState<number | null>(null);
    const [contactsCount, setContactsCount] = useState<number | null>(null);

    const listOfResidents = (userObject && userObject.registrants && userObject.registrants.list) || [];
    const activeRegistrantsIndexes =
        (userObject && userObject.registrants && userObject.registrants.activeRegistrantsIndexes) || [];

    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents for which the user is a relationship manager because neighbors web only supports the resident who is logged in and we can't deselect or change it
    const isRelationshipManager = listOfResidents.some((registrant: Registrant) => registrant.manager);
    const allowImageEdit = isRelationshipManager && activeResident ? true : false;
    // Guests (Speak2 Members) are never linked as relatives, so the relatives lookup always fails for them.
    const isGuestUser = userObject?.userType === 'guest';
    // Google / Apple / Facebook / Amazon logins carry an identities attribute and have no password to change
    // (same check as resolveGuestLoginMethod in guest-complete-profile).
    const isSocialLogin = Boolean(userObject?.cognitoUser?.attributes?.identities);
    const guestPendingReason = userObject?.guestAccess?.pendingReason || '';
    const guestStatusLabel = guestPendingReason === 'verification_required'
        ? 'Approval pending'
        : guestPendingReason === 'subscription_required'
            ? 'Membership required'
            : 'Member';

    useEffect(() => {
        if (isSocialLogin && activeTab === 'password') onTabChange('about');
    }, [isSocialLogin, activeTab]);

    useEffect(() => {
        if (activeResident) {
            setImageUrl(getResidentDisplayImage(activeResident));
        }
    }, [userObject]);

    useEffect(() => {
        if (!activeResident?._id || !activeResident?.Facility) return;
        let cancelled = false;
        setGroupsCount(null);
        setContactsCount(null);

        const loadCounts = async () => {
            const [groupsResult, familyResult] = await settleAll([
                fetchGroups({ Facility: activeResident.Facility, ResidentId: String(activeResident._id) }, 1, 1),
                isGuestUser ? Promise.resolve({ relatives: [] }) : getFamilyList(activeResident._id),
            ]);
            if (cancelled) return;

            let hadError = false;

            if (groupsResult.status === 'fulfilled') {
                setGroupsCount((groupsResult.value as any)?.TotRecords ?? 0);
            } else {
                hadError = true;
                setGroupsCount(0);
            }

            if (familyResult.status === 'fulfilled') {
                setContactsCount(Array.isArray((familyResult.value as any)?.relatives) ? (familyResult.value as any).relatives.length : 0);
            } else {
                hadError = true;
                setContactsCount(0);
            }

            if (hadError) {
                sendToast('error', 'Error loading counts');
            }
        };

        loadCounts();

        return () => {
            cancelled = true;
        };
    }, [activeResident?._id, activeResident?.Facility, isGuestUser]);

    const handleResidentClick = (registrant: Registrant) => {
        // set active resident to clicked one and update setUser with active resident index
        const activeRegistrantsIndexes = [userObject.registrants.list.indexOf(registrant)];
        setUser({ ...userObject, registrants: { ...userObject.registrants, activeRegistrantsIndexes } });
    };

    const onClickImageSave = async () => {
        if (imageEditor && imageName && activeResident) {
            setIsUploadingImage(true);
            setImageUrl('');
            const canvas = imageEditor.getImageScaledToCanvas();
            try {
                const signedUrl = await fetchSignedUrl(activeResident.Facility, residentImageFolder, imageName);
                if (!signedUrl)
                    throw new Error('Could not fetch signed URL');
                const dataUrl = canvas.toDataURL();
                const blob = converterDataURItoBlob(dataUrl);
                const res = await uploadToSignedUrl(blob, signedUrl);

                if (res.ok) {
                    const url = `${speak2CdnDevBucketUrl}/${residentImageFolder}/${imageName}`;
                    const residentId = activeResident._id;
                    const residentPayload: Partial<Registrant> = {
                        SocialProfile: {
                            Image: url,
                        }
                    };
                    const updateRes = await updateResident(residentId, residentPayload);
                    if (!updateRes)
                        throw new Error('Could not update image');
                    setImageUrl(url);
                    setUser(
                        {
                            ...userObject,
                            registrants: {
                                ...userObject.registrants,
                                list: userObject.registrants.list.map((resident: Registrant) => {
                                    if (resident._id === residentId) {
                                        return {
                                            ...resident,
                                            SocialProfile: {
                                                ...resident.SocialProfile,
                                                Image: url
                                            }
                                        };
                                    }
                                    return resident;
                                })
                            }
                        }
                    );
                    setImageModalOpen(false);
                    sendToast('success', 'Profile Image Updated');
                } else {
                    throw new Error('Could not update image. Please try again.');
                }
            } catch (e) {
                sendToast('error', 'Could not update image. Please try again.');
            } finally {
                setIsUploadingImage(false);
            }
        }
        return;
    };

    const activeResidentDisplayName = getResidentDisplayFullName(activeResident);
    const activeResidentRoom = activeResident?.Unit?.Name || '';

    return (
        <>
            <div className="profile-left-col">
                <div className="profile-switcher">
                    <div className="switcher-header">
                        <span className="switcher-label">Switch Profile</span>
                        {!isGuestUser && (
                            <button className="switcher-add" type="button" title="Invite someone" onClick={() => toggleRedirect('/invites')}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            </button>
                        )}
                    </div>
                    <div className="profile-list">
                        {listOfResidents.map((registrant: Registrant, index: number) => {
                            if (!registrant.manager) return null;
                            const isActive = String(activeResident?._id) === String(registrant._id);
                            return (
                                <button
                                    key={index}
                                    className={`profile-list-item${isActive ? ' active' : ''}`}
                                    onClick={() => handleResidentClick(registrant)}
                                    type="button"
                                >
                                    <div className="pli-avatar">
                                        <Avatar
                                            srcs={getResidentDisplayImages(registrant)}
                                            firstName={getResidentDisplayFirstName(registrant)}
                                            lastName={getResidentDisplayLastName(registrant)}
                                            className="pli-avatar-img"
                                        />
                                    </div>
                                    <div className="pli-info">
                                        <div className="pli-name">{getResidentDisplayFullName(registrant)}</div>
                                        <div className="pli-role">{registrant.Unit?.Name || ''}</div>
                                    </div>
                                    <div className="pli-active-dot" />
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="profile-hero">
                    <div className="hero-banner" />
                    <div className="hero-body">
                        <div className="hero-avatar-row">
                            <div
                                className="hero-avatar"
                                onClick={() => allowImageEdit && setImageModalOpen(true)}
                                style={{ cursor: allowImageEdit ? 'pointer' : 'default' }}
                            >
                                <Avatar
                                    src={imageUrl}
                                    firstName={activeResident?.SocialProfile?.FirstName || activeResident?.FirstName}
                                    lastName={activeResident?.SocialProfile?.LastName || activeResident?.LastName}
                                    className="hero-avatar-img"
                                />
                                {allowImageEdit && (
                                    <div className="avatar-overlay">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="hero-name">{activeResidentDisplayName}</div>
                                {activeResidentRoom && (
                                    <div className="hero-room">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        <span>{activeResidentRoom}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="hero-stats">
                            <div className="hstat"><div className="hstat-num">{groupsCount !== null ? groupsCount : <Loader size="mini" active inline inverted />}</div><div className="hstat-label">Groups</div></div>
                            {isGuestUser ? (
                                <div className="hstat"><div className="hstat-num hstat-status">{guestStatusLabel}</div><div className="hstat-label">Status</div></div>
                            ) : (
                                <div className="hstat"><div className="hstat-num">{contactsCount !== null ? contactsCount : <Loader size="mini" active inline inverted />}</div><div className="hstat-label">Contacts</div></div>
                            )}
                        </div>
                    </div>
                </div>

                <nav className="side-nav">
                    <button className={`side-nav-item${activeTab === 'about' ? ' active' : ''}`} type="button" onClick={() => onTabChange('about')}>
                        <div className="side-nav-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                        </div>
                        About Me
                        <div className="nav-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></div>
                    </button>
                    <button className={`side-nav-item${activeTab === 'wellness' ? ' active' : ''}`} type="button" onClick={() => onTabChange('wellness')}>
                        <div className="side-nav-icon wellness">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>
                        </div>
                        Wellness
                        <div className="nav-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></div>
                    </button>
                    {!isSocialLogin && (
                    <button className={`side-nav-item${activeTab === 'password' ? ' active' : ''}`} type="button" onClick={() => onTabChange('password')}>
                        <div className="side-nav-icon password">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </div>
                        Password
                        <div className="nav-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></div>
                    </button>
                    )}
                    {resolvedShowPaymentsEntry && (
                        <button className={`side-nav-item${activeTab === 'payments' ? ' active' : ''}`} type="button" onClick={() => onTabChange('payments')}>
                            <div className="side-nav-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" ry="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                            </div>
                            Payments
                            <div className="nav-arrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></div>
                        </button>
                    )}
                </nav>
            </div>

            <ImageCropModal
                setImageEditor={setImageEditor}
                setModalOpen={setImageModalOpen}
                onClickSave={onClickImageSave}
                open={imageModalOpen}
                loading={isUploadingImage}
                borderRadius={125}
                setImageName={setImageName}
                className="mp-image-crop-modal"
            />
        </>
    );
};

export default AllConnectedResidents;
